// CreateAuthors.jsx - No major changes needed; the error handling already displays the server error message in the UI alert div.
// If you want a browser alert (window.alert) in addition, it can be added in the catch block of handleDelete, but using the UI div is recommended for better UX.
import React, { useState, useEffect } from 'react';
import './CreateAuthors.css';
import './HomeAdmin.css';
import AdminNavbar from './AdminNavbar';


const API_URL = 'https://backend-zonamicroondas.vercel.app/api';

/** Genera slug limpio desde un texto */
const generateSlug = (text = '') =>
  text
    .toLowerCase()
    .trim()
    .replace(/á/g, 'a')
    .replace(/é/g, 'e')
    .replace(/í/g, 'i')
    .replace(/ó/g, 'o')
    .replace(/ú/g, 'u')
    .replace(/ñ/g, 'n')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

export default function CreateAuthors() {
  /* ===== ESTADOS ===== */
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    email: '',
    bio: ''
  });

  useEffect(() => {
    loadAuthors();
  }, []);

  const loadAuthors = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/authors`);
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const data = await res.json();
      if (data.success) {
        setAuthors(data.data || []);
      } else {
        setAuthors([]);
        setError('No se pudo cargar autores');
      }
    } catch (err) {
      setError('Error al cargar autores: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Generar slug automático solo si no se está editando el slug manualmente y es para el nombre
    if (name === 'name' && !editingId && !formData.slug) {
      const newSlug = generateSlug(value);
      setFormData(prev => ({ ...prev, name: value, slug: newSlug }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      if (!formData.name || !formData.email) {
        setError('Nombre y email son obligatorios');
        setLoading(false);
        return;
      }

      // Payload sin avatar_url
      const payload = {
        name: formData.name,
        email: formData.email,
        bio: formData.bio || null,
        slug: formData.slug || generateSlug(formData.name)
      };

      const method = editingId ? 'PUT' : 'POST';
      const url = editingId
        ? `${API_URL}/authors/${editingId}`
        : `${API_URL}/authors`;

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();

      if (!data.success) {
        throw new Error(data.error || 'Error en la operación');
      }

      setSuccess(editingId ? 'Autor actualizado' : 'Autor creado');
      setFormData({ name: '', slug: '', email: '', bio: '' });
      setEditingId(null);
      setShowForm(false);
      await loadAuthors();

      setTimeout(() => setSuccess(null), 2500);
    } catch (err) {
      setError(err.message || 'Error al guardar autor');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (author) => {
    setEditingId(author.id);
    setShowForm(true);
    setFormData({
      name: author.name || '',
      slug: author.slug || '',
      email: author.email || '',
      bio: author.bio || ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setShowForm(false);
    setFormData({ name: '', slug: '', email: '', bio: '' });
  };

  const handleDelete = async (id) => {
    const ok = window.confirm('¿Eliminar este autor? Esta acción no se puede deshacer.');
    if (!ok) return;

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/authors/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Error al eliminar');
      setSuccess('Autor eliminado');
      await loadAuthors();
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err.message || 'Error al eliminar autor');
      // Optional: Add window.alert for emphasis on the error
      // window.alert(err.message || 'Error al eliminar autor');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setShowForm(true);
    setEditingId(null);
    setFormData({ name: '', slug: '', email: '', bio: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="home-container">
      <AdminNavbar />

      {/* ===== CONTENIDO ===== */}
      <main className="main-content">
        <div className="create-authors">
          {showForm && (
            <>
              <div className="create-authors-header">
                <h2>{editingId ? 'Editar Autor' : 'Crear Autor'}</h2>
              </div>

              {error && <div className="alert alert-error">{error}</div>}
              {success && <div className="alert alert-success">{success}</div>}

              <form className="author-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Nombre *</label>
                    <input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Nombre completo"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="correo@ejemplo.com"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Slug (editable)</label>
                    <input
                      name="slug"
                      value={formData.slug}
                      onChange={handleInputChange}
                      placeholder="slug-ejemplo (se genera automáticamente)"
                    />
                    <small>El slug se usa en URLs; si lo dejas vacío se generará automáticamente.</small>
                  </div>
                </div>

                <div className="form-group">
                  <label>Bio</label>
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    placeholder="Pequeña biografía o descripción"
                    rows="3"
                  />
                </div>

                <div className="form-actions">
                  {editingId ? (
                    <>
                      <button type="button" className="btn btn-secondary" onClick={handleCancelEdit} disabled={loading}>
                        Cancelar
                      </button>
                      <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? 'Guardando...' : 'Actualizar Autor'}
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" className="btn btn-secondary" onClick={handleCancelEdit} disabled={loading}>
                        Cancelar
                      </button>
                      <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? 'Creando...' : 'Crear Autor'}
                      </button>
                    </>
                  )}
                </div>
              </form>

              <hr />
            </>
          )}

          <div className="authors-list">
            <div className="list-header">
              <h3>Autores Registrados</h3>
              {!showForm && (
                <button className="btn btn-primary" onClick={handleCreateNew}>
                  Crear Nuevo Autor
                </button>
              )}
            </div>
            {loading && authors.length === 0 ? (
              <div className="loading">Cargando autores...</div>
            ) : authors.length === 0 ? (
              <div className="no-data">No hay autores</div>
            ) : (
              <table className="authors-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Email</th>
                    <th>Slug</th>
                    <th>Bio</th>
                    <th className="centered">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {authors.map(a => (
                    <tr key={a.id}>
                      <td>{a.name}</td>
                      <td>{a.email}</td>
                      <td>{a.slug}</td>
                      <td className="small-cell">
                        {a.bio ? (a.bio.length > 80 ? `${a.bio.slice(0, 77)}...` : a.bio) : '-'}
                      </td>
                      <td className="centered">
                        <button className="btn btn-secondary" onClick={() => handleEdit(a)}>
                          Editar
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={() => handleDelete(a.id)}
                          style={{ marginLeft: 8 }}
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}